import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Settings from '../Settings';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import SchemaForm from '@afosto/app-components-react/SchemaForm';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const SERVICE_POINT_SETTINGS = 'afosto.tenant.lcs.locations.v1.servicepoints';

const WEEKDAYS_SHORT_NAMES = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];

const ServicePointSettings = ({ pathID }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['lcs:locations:read']);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [formSchema, setFormSchema] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const hasReadPermissions = hasPermissions();

  const uiSchema = {
    servicePoints: {
      props: {
        label: intl.formatMessage(translations.editDrawer.servicePoints.label),
        shouldRemoveId: false,
        prefillId: false,
      },
      id: {
        component: 'FulfilmentLocationSearch',
        props: {
          label: intl.formatMessage(translations.editDrawer.location.label),
        },
      },
      name: {
        props: {
          label: intl.formatMessage(translations.editDrawer.name.label),
        },
      },
      openings: {
        component: 'OpeningHours',
        props: {
          label: intl.formatMessage(translations.editDrawer.openings.label),
        },
      },
    },
  };

  const handleUpdateProfile = async fields => {
    try {
      const servicePoints = (fields?.servicePoints ?? []).reduce((acc, servicePoint) => {
        const formattedOpenings = Object.keys(servicePoint?.openings ?? {}).reduce(
          (openingsAcc, day) => {
            const weekday = WEEKDAYS_SHORT_NAMES.findIndex(shortName => shortName === day);
            const windows = servicePoint?.openings[day].map(openingsTime => ({
              start: openingsTime.opening,
              end: openingsTime.closing,
            }));

            return [...openingsAcc, { weekday, windows }];
          },
          [],
        );

        return [
          ...acc,
          { ...servicePoint, id: servicePoint?.id?.value, openings: formattedOpenings },
        ];
      }, []);

      await profileApi.updateProfile(
        SERVICE_POINT_SETTINGS.replace('tenant', pathID),
        servicePoints,
      );
      setSettings(fields);
      enqueueSnackbar(intl.formatMessage(translations.feedback.servicePointSettingsUpdated), {
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.servicePointSettingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const handleFetchProfile = async () => {
      try {
        const { data: profileData = [] } = await profileApi.getProfile(
          SERVICE_POINT_SETTINGS.replace('tenant', pathID),
        );
        const locationsResponse = await fulfilmentApi.listFulfilmentLocations({ isDisabled: 0 });
        const { data: locations = [] } = locationsResponse;
        const formattedLocations = locations.map(location => ({
          ...location,
          label: location.name,
          value: location.id,
        }));

        const servicePoints = profileData.reduce((acc, data) => {
          const location = findObjectInArray(formattedLocations, 'id', data.id);
          const formattedOpenings = (data?.openings ?? []).reduce((openingsAcc, opening) => {
            const windows = (opening?.windows ?? []).map(window => ({
              opening: window.start,
              closing: window.end,
            }));

            return {
              ...openingsAcc,
              [WEEKDAYS_SHORT_NAMES[opening.weekday]]: windows,
            };
          }, {});

          return [...acc, { ...data, id: location, openings: formattedOpenings }];
        }, []);

        if (isMounted) {
          setSettings({
            servicePoints,
          });
        }
      } catch (error) {
        if (isMounted) {
          setSettings(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchProfileFormSchema = async () => {
      try {
        const { data = {} } = await profileApi.getProfileDefinitions(
          SERVICE_POINT_SETTINGS.replace('tenant', pathID),
        );
        if (isMounted) {
          const itemsSchema = {
            ...data.items,
            properties: {
              ...data.items.properties,
              id: { type: 'object' },
              openings: { type: 'object' },
            },
          };

          setFormSchema({
            type: 'object',
            properties: {
              servicePoints: {
                type: data.type,
                items: itemsSchema,
              },
            },
          });
        }
      } catch (error) {
        if (isMounted) {
          setFormSchema({});
        }
      }
    };

    if (hasReadPermissions) {
      handleFetchProfile();
      fetchProfileFormSchema();
    }

    return () => {
      isMounted = false;
    };
  }, [pathID, hasReadPermissions]);

  if (!hasReadPermissions) {
    return (
      <Settings title={intl.formatMessage(translations.title)} isLoading={false}>
        <UnauthorizedComponent />
      </Settings>
    );
  }

  return (
    <>
      <Settings title={intl.formatMessage(translations.title)} isLoading={isLoading}>
        <List dense disablePadding>
          <ListItem>
            <ListItemText
              primary={intl.formatMessage(translations.display.label)}
              secondary={(settings?.servicePoints ?? []).map(setting => setting.name).join(', ')}
            />
            {hasPermissions(['lcs:locations:write']) && (
              <Button onClick={() => setIsEditing(true)} variant="outlined" size="small">
                <FormattedMessage {...translations.actions.edit} />
              </Button>
            )}
          </ListItem>
        </List>
      </Settings>
      <FormDrawer
        isOpen={isEditing}
        title={intl.formatMessage(translations.editDrawer.title)}
        drawerWidth="420px"
        onClose={() => setIsEditing(false)}
      >
        {formSchema && (
          <SchemaForm
            schema={formSchema}
            uiSchema={uiSchema}
            values={settings}
            onSubmit={handleUpdateProfile}
            onCancel={() => setIsEditing(false)}
          />
        )}
      </FormDrawer>
    </>
  );
};

ServicePointSettings.propTypes = {
  pathID: PropTypes.string,
};

ServicePointSettings.defaultProps = {
  pathID: 'tenant',
};

export default ServicePointSettings;
